/**
 * Two people's answers, side by side. The one view for it.
 *
 * Rendered identically in the two places answers are ever shown — a match, and
 * a public profile whose password you hold — because they are the same act with
 * different authorisation in front of them, and a reader who learns to read one
 * has learnt to read the other. Neither page has its own version.
 *
 * Two ways through the same rows, and they answer two different questions:
 *
 *   By area          the existing per-area bars, now with lids on them. Open
 *                    one to see the answers that produced its percentage. This
 *                    is for "we scored 61% on food — on what?"
 *   Closest first    every row in one list, agreements at the top and opposed
 *                    answers at the bottom, banded with headings. This is for
 *                    "what would I actually be signing up for", which is the
 *                    question somebody with a decision to make is asking.
 *
 * The ordering rule and the bands both live in shared/compare, so the sort is
 * the same in both modes and the same on the server if it ever needs to be.
 *
 * DENSITY
 * -------
 * A row is one line. Sixty questions is a normal overlap, and the job here is a
 * glance down a list rather than a study of any single row — anything that
 * turns each one into a card means scrolling past four at a time and losing the
 * shape of the thing. So: a 56px sparkline of where the two sliders sat, the
 * prompt, and both answers in words. No per-row badge, because the sparkline
 * already shows the gap and the headings already name it; no per-row "You" and
 * "Them" labels, because the colours say so once at the top.
 *
 * DRAWING TWO PEOPLE ON ONE TRACK
 * -------------------------------
 * You are `--brand`, they are `--brand-alt` — the two ends of the gradient
 * every score bar in the app is already painted with, so the pairing is
 * familiar before it's explained. Colour alone never carries it: your marker is
 * solid and theirs is a ring, both answers are spelled out in words, and where
 * both landed on the same stop the single marker is split down the middle
 * rather than one silently hiding the other.
 */

import { ChevronRight, Link2, Star, TrendingDown } from "lucide-react";
import { useMemo, useState } from "react";

import { GroupScoreRow } from "@/components/scores";
import { cn } from "@/lib/utils";
import {
  BANDS,
  bandOf,
  byGroup,
  distanceOf,
  labelsOf,
  sortByDifference,
  tally,
  type AnswerComparison,
  type BandId,
} from "@/shared/compare";
import { IMPORTANCE_LABELS, SCALE_MAX, SCALE_MIN, type Importance } from "@/shared/scale";
import type { GroupScore } from "@/shared/types";

/** Percentage along the track for a value, 0..100. Same rule as ScaleSlider. */
const positionOf = (v: number) => ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;

/** How strongly the segment between the two markers is drawn. */
const BAND_SEGMENT: Record<BandId, string> = {
  same: "var(--brand)",
  near: "color-mix(in oklab, var(--brand) 55%, transparent)",
  apart: "color-mix(in oklab, var(--muted-foreground) 45%, transparent)",
  opposed: "color-mix(in oklab, var(--negative) 60%, transparent)",
};

// ---------------------------------------------------------------------------
// One row
// ---------------------------------------------------------------------------

/** Only ever drawn for a stated importance, so an ordinary row stays quiet. */
function ImportanceMark({ importance, who }: { importance: Importance; who: string }) {
  if (importance === 0) return null;
  const Icon = importance === 1 ? Star : TrendingDown;
  return (
    <Icon
      aria-label={`${who}: ${IMPORTANCE_LABELS[importance].toLowerCase()}`}
      className={cn(
        "ml-0.5 inline-block size-2.5 shrink-0 align-[-1px]",
        importance === 1 ? "fill-current text-brand" : "text-muted-foreground/50",
      )}
    />
  );
}

/**
 * Where the two of them sat, in 56 pixels.
 *
 * Small enough to lead a one-line row and still show the three things that
 * matter: how far apart, which way round, and whether either of them actually
 * cared — the middle notch is the "doesn't move me" point, so a marker resting
 * on it reads as a shrug without needing to say so.
 */
function MiniTrack({ row }: { row: AnswerComparison }) {
  const mine = positionOf(row.yours);
  const theirs = positionOf(row.theirs);
  const [from, to] = mine <= theirs ? [mine, theirs] : [theirs, mine];
  const together = row.yours === row.theirs;
  const gap = distanceOf(row);

  return (
    <span
      aria-hidden
      title={gap === 0 ? "The same answer" : `${gap} stop${gap === 1 ? "" : "s"} apart`}
      className="relative block h-2.5 w-14 shrink-0"
    >
      <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-muted" />
      {!together && (
        <span
          className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full"
          style={{ left: `${from}%`, width: `${to - from}%`, background: BAND_SEGMENT[bandOf(row).id] }}
        />
      )}
      <span className="absolute left-1/2 top-1/2 h-1.5 w-px -translate-x-1/2 -translate-y-1/2 bg-border" />

      {together ? (
        // One marker for two answers, split down the middle. Stacking them
        // would hide one side entirely and read as a half-answered row.
        <span
          className="absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-1 ring-background"
          style={{
            left: `${mine}%`,
            background: "linear-gradient(90deg, var(--brand) 50%, var(--brand-alt) 50%)",
          }}
        />
      ) : (
        <>
          <span
            className="absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand ring-1 ring-background"
            style={{ left: `${mine}%` }}
          />
          <span
            className="absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-brand-alt bg-background"
            style={{ left: `${theirs}%` }}
          />
        </>
      )}
    </span>
  );
}

function CompareRow({ row, theirName }: { row: AnswerComparison; theirName: string }) {
  const words = labelsOf(row);

  return (
    // Wrapping rather than truncating: below about 420px the two answers drop
    // to their own line instead of the prompt losing its last three words,
    // which is the half of the row you can't reconstruct from anything else.
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 border-b border-border/40 py-1.25 last:border-0">
      <MiniTrack row={row} />
      <span className="min-w-0 flex-1 basis-40 text-[13px] leading-snug">
        {row.prompt}
        {row.theirPrompt && (
          /* A complementary pair fits *because* the two answers are to
             different questions — unsaid, the row reads as the opposite of
             what it is. Compressed to the arrow and the other prompt. */
          <span className="ml-1.5 whitespace-nowrap text-[11.5px] text-brand">
            <Link2 className="inline size-2.5 align-[-1px]" aria-hidden /> {row.theirPrompt}
          </span>
        )}
      </span>
      <span className="ml-auto shrink-0 text-right text-[12px] leading-snug">
        <span className="font-medium text-brand">{words.yours}</span>
        <ImportanceMark importance={row.yourImportance} who="You" />
        <span className="mx-1.5 text-muted-foreground/40">·</span>
        <span className="font-medium text-brand-alt">{words.theirs}</span>
        <ImportanceMark importance={row.theirImportance} who={theirName} />
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The two arrangements
// ---------------------------------------------------------------------------

function Rows({ rows, theirName }: { rows: AnswerComparison[]; theirName: string }) {
  return (
    <div>
      {rows.map((row) => (
        <CompareRow
          // A complementary pair contributes a row in each direction, so the
          // question id alone is not unique within one list.
          key={`${row.questionId}:${row.theirQuestionId ?? row.questionId}`}
          row={row}
          theirName={theirName}
        />
      ))}
    </div>
  );
}

function ByArea({
  groups,
  rows,
  theirName,
}: {
  groups: GroupScore[];
  rows: AnswerComparison[];
  theirName: string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const grouped = useMemo(() => byGroup(rows), [rows]);

  // Driven by the score list rather than by the rows, so the areas and their
  // order are exactly the ones the bars above have always shown. An area with
  // a score but no rows — a private one withheld by a password — still draws,
  // just without a lid.
  const withRows = new Map(grouped.map((g) => [g.groupKey, g]));

  return (
    <div className="space-y-3">
      {groups
        .filter((g) => g.score !== null)
        .map((g) => {
          const detail = withRows.get(g.groupKey);
          const isOpen = open === g.groupKey;
          return (
            <GroupScoreRow
              key={g.groupKey}
              group={g}
              open={isOpen}
              onToggle={detail ? () => setOpen(isOpen ? null : g.groupKey) : undefined}
            >
              {detail && (
                <div className="mt-1 rounded-xl border border-border/60 bg-background/40 px-3.5 sm:px-4">
                  <Rows rows={detail.rows} theirName={theirName} />
                </div>
              )}
            </GroupScoreRow>
          );
        })}
    </div>
  );
}

function ClosestFirst({ rows, theirName }: { rows: AnswerComparison[]; theirName: string }) {
  const sorted = useMemo(() => sortByDifference(rows), [rows]);

  return (
    <div>
      {BANDS.map((band) => {
        const inBand = sorted.filter((r) => bandOf(r).id === band.id);
        if (inBand.length === 0) return null;
        return (
          <div key={band.id} className="mt-5 first:mt-0">
            <h4 className="mb-1 flex items-baseline gap-2 text-[12px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
              {band.heading}
              <span className="text-[11.5px] font-normal tabular-nums text-muted-foreground/70">
                {inBand.length}
              </span>
            </h4>
            <Rows rows={inBand} theirName={theirName} />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------

const MODES = [
  { id: "area", label: "By area" },
  { id: "difference", label: "Closest first" },
] as const;

export function AnswerCompare({
  rows,
  groups,
  theirName,
  withheld = [],
  className,
}: {
  rows: AnswerComparison[];
  /** The per-area bars this view puts lids on. */
  groups: GroupScore[];
  theirName: string;
  /** Areas deliberately not included, so a short list doesn't read as a thin one. */
  withheld?: { groupKey: string; groupLabel: string; emoji: string }[];
  className?: string;
}) {
  const [mode, setMode] = useState<(typeof MODES)[number]["id"]>("area");
  const counts = useMemo(() => tally(rows), [rows]);

  if (rows.length === 0) {
    return (
      <p className={cn("text-[14px] leading-relaxed text-muted-foreground", className)}>
        Nothing to compare yet — this shows the questions you have{" "}
        <em className="not-italic">both</em> answered, and there aren't any.
      </p>
    );
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-muted-foreground">
          <span className="font-medium text-foreground">{counts.total}</span> questions you
          both answered ·{" "}
          <span className="font-medium text-brand">{counts.same + counts.near}</span> as good
          as agreed ·{" "}
          <span className={cn("font-medium", counts.opposed > 0 && "text-negative")}>
            {counts.apart + counts.opposed}
          </span>{" "}
          apart
        </p>

        <div
          role="tablist"
          aria-label="How to arrange the answers"
          className="flex shrink-0 items-center gap-0.5 rounded-full border border-border/70 bg-card/60 p-1"
        >
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={mode === m.id}
              onClick={() => setMode(m.id)}
              className={cn(
                "rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors",
                mode === m.id
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        {mode === "area" ? (
          <ByArea groups={groups} rows={rows} theirName={theirName} />
        ) : (
          <ClosestFirst rows={rows} theirName={theirName} />
        )}
      </div>

      {mode === "area" && (
        <p className="mt-4 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
          <ChevronRight className="size-3.5 shrink-0" aria-hidden />
          Open an area to read the answers behind its percentage.
        </p>
      )}

      {withheld.length > 0 && (
        <p className="mt-4 rounded-xl border border-border/60 bg-muted/40 px-3.5 py-3 text-[12.5px] leading-relaxed text-muted-foreground">
          Not included:{" "}
          {withheld.map((g, i) => (
            <span key={g.groupKey}>
              {i > 0 && ", "}
              <span aria-hidden>{g.emoji}</span> {g.groupLabel}
            </span>
          ))}
          . {theirName} kept those areas out of what this password opens — they still count
          towards the score above.
        </p>
      )}
    </div>
  );
}
